import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { StripeConnection } from "@/components/settings/StripeConnection";
import { TrackedProducts } from "@/components/settings/TrackedProducts";
import type { StripeSettingsInfo, TrackedProduct } from "@/lib/types";

type State = {
  stripeConnection: StripeSettingsInfo;
  hubspotConnected: boolean;
  hasProducts: boolean;
  products: TrackedProduct[];
};

export function SettingsPage() {
  const [state, setState] = useState<State | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/settings/state", {
          credentials: "include",
        });
        if (!res.ok) throw new Error(`Failed to load (${res.status})`);
        const data = (await res.json()) as State;
        if (!cancelled) setState(data);
      } catch (err) {
        if (!cancelled)
          setError(err instanceof Error ? err.message : "Failed to load");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="mx-auto max-w-4xl p-8">
      <div className="mb-8">
        <h1 className="text-2xl font-semibold tracking-tight">Settings</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Configure integrations and metric sources.
        </p>
      </div>

      {error && (
        <div className="mb-6 rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {!state ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="space-y-6">
          <StripeConnection connection={state.stripeConnection} />
          <TrackedProducts initialProducts={state.products} />
        </div>
      )}
    </div>
  );
}
